"use client"

import { createContext, type ReactNode } from "react"

import { FacetChipRow } from "@/components/marketplace/listing/facet-chip-row"
import { FilterBar } from "@/components/marketplace/listing/filter-bar"
import { FilterSidebar } from "@/components/marketplace/listing/filter-sidebar"
import { ListingHeader } from "@/components/marketplace/listing/listing-header"
import type { FacetOption } from "@/components/marketplace/listing/types"
import { InfiniteProductList } from "@/components/marketplace/product/infinite-product-list"
import type { Product } from "@/components/marketplace/types"
import { Breadcrumbs, Stack } from "@/components/ui"
import type { Crumb } from "@/components/ui"
import { useDelayedFlag } from "@/lib/hooks/use-delayed-flag"
import type { ListingPagination } from "@/lib/listing-pagination"
import type { SearchFacet, SearchQuery } from "@/lib/types/search"

export const ListingEmptyHeadingContext = createContext<"h1" | "h2">("h1")

interface ListingShellProps {
  breadcrumbs?: Crumb[]
  title: ReactNode
  subline?: ReactNode
  countKeyword?: string
  banner?: ReactNode
  query: SearchQuery
  categoryOptions?: FacetOption[]
  categoryLabels?: Record<string, string>
  facets?: SearchFacet[]
  onUpdateQuery: (updates: Partial<SearchQuery>) => void
  products: Product[]
  renderedQuery?: string
  totalCount?: number
  hasMore?: boolean
  loading?: boolean
  refreshing?: boolean
  onLoadMore?: () => void
  previousPageHref?: string
  nextPageHref?: string
  empty?: ReactNode
  error?: ReactNode
  onProductClick?: (product: Product, index: number) => void
  pages?: { serveId?: string; products: Product[] }[]
  pagination?: ListingPagination
}

export const ListingShell = ({
  breadcrumbs,
  title,
  subline,
  countKeyword,
  banner,
  query,
  categoryOptions,
  categoryLabels,
  facets,
  onUpdateQuery,
  products,
  renderedQuery,
  totalCount,
  hasMore,
  loading,
  refreshing,
  onLoadMore,
  previousPageHref,
  nextPageHref,
  empty,
  error,
  onProductClick,
  pages,
  pagination,
}: ListingShellProps) => {
  const isEmpty = !loading && products.length === 0
  const activeCount =
    query.categories.length +
    Object.values(query.attributes).reduce((total, values) => total + values.length, 0) +
    (query.priceMin !== undefined || query.priceMax !== undefined ? 1 : 0) +
    (query.inStockOnly === false ? 1 : 0)
  const clearAll = () => onUpdateQuery({ categories: [], collections: [], attributes: {}, priceMin: undefined, priceMax: undefined, inStockOnly: true })
  const dimmed = useDelayedFlag(Boolean(refreshing))
  const showError = error && products.length === 0

  if (isEmpty && empty && !showError && activeCount === 0) {
    return (
      <div className="max-w-page @container mx-auto w-full">
        <div className="px-gut pt-500 pb-400 @3xl:pt-750">{empty}</div>
      </div>
    )
  }

  const header = (actions?: ReactNode) => (
    <ListingHeader breadcrumbs={breadcrumbs} title={title} subline={subline} count={totalCount} countKeyword={countKeyword} actions={actions} />
  )

  return (
    <div className="max-w-page @container mx-auto w-full">
      <div className="px-gut py-400">
        {showError ? (
          <>
            {header()}
            <div className="mt-600">{error}</div>
          </>
        ) : (
          <div className="@5xl:gap-rail-gap @5xl:flex">
            <div className="@5xl:max-h-pinned @5xl:top-sticky-page @5xl:w-rail hidden @5xl:sticky @5xl:block @5xl:shrink-0 @5xl:self-start @5xl:overflow-y-auto @5xl:overscroll-contain @5xl:pb-400">
              <FilterSidebar query={query} categoryOptions={categoryOptions} facets={facets} onUpdateQuery={onUpdateQuery} />
            </div>

            <div className="min-w-0 @5xl:flex-1">
              {banner ? (
                <Stack gap="300">
                  {breadcrumbs && breadcrumbs.length > 0 && <Breadcrumbs items={breadcrumbs} />}
                  {banner}
                  <FacetChipRow query={query} onUpdateQuery={onUpdateQuery} className="mt-200 hidden @5xl:flex" />
                </Stack>
              ) : (
                header(<FacetChipRow query={query} onUpdateQuery={onUpdateQuery} className="hidden min-w-0 justify-end @5xl:flex" />)
              )}

              <FilterBar
                query={query}
                categoryOptions={categoryOptions}
                categoryLabels={categoryLabels}
                facets={facets}
                filterCount={activeCount}
                resultCount={totalCount}
                onUpdateQuery={onUpdateQuery}
                onClearAll={clearAll}
                showActiveFilters={!(isEmpty && empty)}
                className="mt-400 @5xl:hidden"
              />

              {isEmpty && empty ? (
                <ListingEmptyHeadingContext value="h2">
                  <div className="py-400">{empty}</div>
                </ListingEmptyHeadingContext>
              ) : (
                <div aria-busy={refreshing} className={`duration-quick mt-500 transition-opacity @5xl:mt-700 ${dimmed ? "pointer-events-none opacity-40" : ""}`}>
                  <InfiniteProductList
                    bare
                    title={null}
                    products={products}
                    renderedQuery={renderedQuery}
                    totalCount={totalCount}
                    hasMore={hasMore}
                    loading={loading}
                    onLoadMore={onLoadMore}
                    previousPageHref={previousPageHref}
                    nextPageHref={nextPageHref}
                    autoLoad
                    onProductClick={onProductClick}
                    pages={pages}
                    pagination={pagination}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
